import classnames from 'classnames';
import gsap from 'gsap';
import _ from 'lodash';
import React from 'react';

import { useClickAway } from 'react-use';

import { useRouter } from 'next/router';

import qs from 'qs';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';

import {
  CircleArrowSvg,
  MicroscopeSvg,
  RankingSvg,
  TrashSvg,
} from 'components/SorraSvg';

import api, { swrFetcher } from 'utils/api';
import { swrGoFetcher } from 'utils/go_api';
// import { getProductOverallRanks } from 'utils/voting2024';
import { getProductOverallRanks } from 'utils/voting2025';

import localStore from 'utils/localStore';

import { MixpanelConfig } from 'utils/mixpanelConfig';
import { recordSearchToAnalytics } from 'utils/searchAnalytics';

import { useAppLaunch } from 'contexts/AppLaunch';
import { useUserAuth } from 'contexts/UserAuth';

import BeautyAwardBadge from 'components/BeautyAwardBadge';
import MobileSearchModal from 'components/MobileSearchModal';
import MonthlyRankingBadge from 'components/MonthlyRankingBadge';
import useRWD from 'hooks/useRWD';

const { useState, useEffect, useCallback, useRef } = React;

const SearchInput = (props) => {
  const { showSearchModal, setShowSearchModal, withoutOverlay = true } = props;

  const { isLg } = useRWD();
  const { isSignInStatusReady, user, getAuthorizationHeader } = useUserAuth();

  const router = useRouter();
  const searchInputRef = useRef(null);
  const searchAreaRef = useRef(null);

  useClickAway(searchAreaRef, () => {
    if (isLg) {
      setShowSearchModal(false);
      setSearchPlaceholder('');
      setShowClearHistory(false);
      searchInputRef.current.blur();
    }
  });

  const { secondCategoryMap, getMonthlyRanks } = useAppLaunch();

  const [localSearchHistory, setLocalSearchHistory] = useState([]);
  useEffect(() => {
    if (isSignInStatusReady && !user) {
      const h = localStore.getSorraSearchHistory();
      setLocalSearchHistory(h);
    }
  }, [isSignInStatusReady, user]);

  const {
    data: searchHistory,
    mutate: mutateSearchHistory,
    isLoading: isSearchHistoryLoading,
  } = useSWR(
    user ? '/api/v3/search/history' : null,
    async (url) => {
      const r = await swrFetcher(url, getAuthorizationHeader);
      return _.filter(r, (h) => {
        return !_.isEmpty(_.trim(h));
      });
    },
    {
      fallbackData: localSearchHistory,
    }
  );

  const { data: topSearches, isLoading: isTopSearchesLoading } = useSWR(
    '/api/v1/search/top',
    async (url) => {
      const data = await swrGoFetcher(url);
      return _.take(data, isLg ? 12 : 8);
    },
    {
      fallbackData: [],
    }
  );

  const [keyword, setKeyword] = useState('');
  const searchQuery = qs.stringify({
    keyword: _.trim(keyword),
  });
  const [instantKeyword, setInstantKeyword] = useState('');
  const debouncedSearchKeyword = useCallback(
    _.debounce((value) => {
      setKeyword(value);
    }, 300),
    [setKeyword]
  );

  useEffect(() => {
    debouncedSearchKeyword(instantKeyword);
  }, [instantKeyword]);
  const { data: recommendations, isLoading: isRecommendationsLoading } = useSWR(
    `/api/v1/search/recommendations?${searchQuery}`,
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const recommendationSecondCategories = _.reduce(
    recommendations,
    (acc, rec) => {
      const { second_categories } = rec;
      return _.uniq([...acc, ...second_categories]);
    },
    []
  );

  const [topSearchDisplayIdx, setTopSearchDisplayIdx] = useState(0);
  const [topSearchDisplayCount, setTopSearchDisplayCount] = useState(0);

  const [searchPlaceholder, setSearchPlaceholder] = useState('');

  const [showClearHistory, setShowClearHistory] = useState(false);

  useEffect(() => {
    setTopSearchDisplayCount(topSearches.length);
  }, [topSearches]);

  useEffect(() => {
    setTopSearchDisplayIdx(0);
    if (topSearchDisplayCount > 0) {
      if (showSearchModal && _.isEmpty(searchPlaceholder)) {
        setSearchPlaceholder(topSearches[0]);
      }
    }
  }, [topSearchDisplayCount]);

  useEffect(() => {
    if (
      topSearchDisplayIdx === 0 &&
      gsap.getProperty('.search-display', 'y') !== 0
    ) {
      gsap.to('.search-display', {
        y: -32 * topSearchDisplayCount,
        duration: 0.4,
        onComplete: () => {
          gsap.set('.search-display', {
            y: 0,
          });
        },
      });
    } else {
      gsap.to('.search-display', {
        y: -32 * topSearchDisplayIdx,
        duration: 0.4,
      });
    }
  }, [topSearchDisplayIdx, topSearchDisplayCount]);

  useEffect(() => {
    let interval = null;
    if (!showSearchModal) {
      interval = setInterval(() => {
        setTopSearchDisplayIdx((prev) => {
          return (prev + 1) % topSearchDisplayCount;
        });
      }, 3000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showSearchModal, topSearchDisplayCount]);

  useEffect(() => {
    if (showSearchModal) {
      document.querySelector('#app-wrapper').style.overflow = 'hidden';
    } else {
      document.querySelector('#app-wrapper').style.overflow = '';
    }
  }, [showSearchModal]);

  const routeToSearchPage = (keyword, secondCategoryId) => {
    if (!_.isEmpty(keyword)) {
      const mo = [keyword];
      const q = { keyword };
      if (!_.isNil(secondCategoryId) && secondCategoryMap[secondCategoryId]) {
        const sc = secondCategoryMap[secondCategoryId];
        q.categoryID = sc.id;
        q.category = sc.name;
        mo.unshift(sc.name);
      }

      MixpanelConfig.track('search_performed', { search: mo.join(' • ') });
      // 只有這裡算「使用者主動送出的搜尋」，才計入 Algolia
      recordSearchToAnalytics(keyword);
      router.push(`/search?${qs.stringify(q)}`);
      setShowSearchModal(false);
    }
  };

  const handleSearchEnter = () => {
    if (!keyword && topSearches[topSearchDisplayIdx]) {
      routeToSearchPage(topSearches[topSearchDisplayIdx]);
    } else if (!isRecommendationsLoading && recommendations.length) {
      routeToSearchPage(keyword);
    }
  };

  const handleClearSearchHistory = async () => {
    if (user) {
      const headers = await getAuthorizationHeader();
      await api.request({
        method: 'DELETE',
        url: '/api/v3/search/history',
        headers,
      });
      mutateSearchHistory();
      setShowClearHistory(false);
    } else {
      localStore.deleteSorraSearchHistory();
      setLocalSearchHistory([]);
    }
  };

  return (
    <>
      <MobileSearchModal
        show={showSearchModal && (withoutOverlay ? !isLg : true)}
        setShow={setShowSearchModal}
        topSearchDisplayIdx={topSearchDisplayIdx}
      />
      <div ref={searchAreaRef} className="relative h-full w-full px-4 lg:px-0">
        <div
          className={classnames(
            'relative flex h-full w-full items-center gap-x-4 rounded-lg border border-[#D1D5DB] bg-white px-4 py-2',
            {
              'border border-sorra-pink': showSearchModal,
            }
          )}
        >
          <div className="flex min-w-0 grow items-center">
            <div className="h-6 w-6 shrink-0">
              <MicroscopeSvg />
            </div>
            <div className="relative h-8 min-w-0 grow">
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 top-0 overflow-hidden">
                <div
                  className={classnames(
                    'search-display absolute left-4 top-0 text-base font-medium text-[#9B9B9B]',
                    {
                      hidden: showSearchModal || !_.isEmpty(instantKeyword),
                    }
                  )}
                >
                  {topSearches.map((search, idx) => {
                    return (
                      <div key={idx} className="flex h-8 items-center">
                        <p>{search}</p>
                      </div>
                    );
                  })}
                  {topSearches.length > 0 && (
                    <div className="flex h-8 items-center">
                      <p>{topSearches[0]}</p>
                    </div>
                  )}
                </div>
              </div>
              <input
                ref={searchInputRef}
                className="search-input h-full w-full bg-transparent pl-4 text-base font-medium text-[#111928] placeholder:text-[#9B9B9B] focus:outline-none"
                placeholder={showSearchModal ? searchPlaceholder : ''}
                value={instantKeyword}
                onChange={(e) => {
                  setInstantKeyword(e.target.value);
                }}
                onFocus={() => {
                  setSearchPlaceholder(
                    _.get(topSearches, topSearchDisplayIdx) || ''
                  );
                  setShowSearchModal(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.keyCode === 13) {
                    handleSearchEnter();
                  }
                }}
                // onBlur={() => {}}
              ></input>
            </div>
          </div>
          {showSearchModal && (
            <button
              className={classnames(
                'hidden h-full shrink-0 items-center gap-x-3 rounded-lg bg-sorra-pink px-8 py-3 font-medium text-white lg:flex',
                {
                  '!bg-[#9B9B9B]':
                    keyword &&
                    !isRecommendationsLoading &&
                    recommendations.length === 0,
                }
              )}
              disabled={
                keyword &&
                !isRecommendationsLoading &&
                recommendations.length === 0
              }
              onClick={() => {
                handleSearchEnter();
              }}
            >
              <div className="h-5 w-5">
                <MicroscopeSvg color="white" />
              </div>
              <p>搜索</p>
            </button>
          )}
        </div>
        <div
          className={classnames(
            'mt-4 hidden overflow-hidden rounded-lg border border-[#E7E7E7] bg-white shadow-sm lg:block',
            {
              '!hidden': !showSearchModal,
            }
          )}
        >
          <div>
            {keyword && isRecommendationsLoading && (
              <div className="flex w-full items-center justify-center py-11">
                <img
                  className="h-[145px] w-[145px]"
                  src="/images/search-placeholder.gif"
                ></img>
              </div>
            )}
            {keyword &&
              !isRecommendationsLoading &&
              recommendations.length === 0 && (
                <div className="flex w-full flex-col items-center justify-center gap-y-3 py-11">
                  <img
                    className="h-[73px] w-[73px]"
                    src="/svgs/search-empty.svg"
                  ></img>
                  <div className="font-medium text-sorra-gray-800">
                    沒有搜索結果...
                  </div>
                  <a
                    href="/nominate"
                    className="rounded-lg border border-sorra-pink px-4 py-2 text-sm font-medium text-sorra-pink"
                  >
                    立即提名品牌或產品，即時評價賺分！
                  </a>
                </div>
              )}
            {!isRecommendationsLoading && recommendations.length > 0 && (
              <div className="pb-8 pt-4">
                <div className="flex flex-col gap-y-4 px-8 pb-4 text-sm font-medium text-[#4a4a4a]">
                  {_.take(recommendations, 5).map((recommendation, idx) => {
                    const { id, brand, name, en_name, matchedWords } =
                      recommendation;
                    const overallRank = _.head(getProductOverallRanks(id));
                    const monthlyRank = _.head(getMonthlyRanks(id));

                    const display = `${brand ? `${brand} ` : ''} ${name || en_name}`;
                    return (
                      <div
                        key={id}
                        className="flex cursor-pointer flex-row items-center gap-x-2"
                        onClick={async () => {
                          const newKeyword = display;
                          if (user) {
                            try {
                              const headers = await getAuthorizationHeader();
                              await api.request({
                                method: 'post',
                                url: '/api/v3/search/history',
                                headers,
                                data: {
                                  keyword: newKeyword,
                                },
                              });
                            } catch (_) {
                              // console.error(error);
                            }
                          } else {
                            localStore.addSorraSearchHistory(newKeyword);
                          }
                          window.location.href = `/product/${id}`;
                        }}
                      >
                        <Highlighter
                          highlightClassName="text-sorra-pink bg-transparent"
                          textToHighlight={display}
                          searchWords={matchedWords}
                        />
                        {overallRank && (
                          <BeautyAwardBadge
                            size="s"
                            year={overallRank.year}
                            rank={overallRank.rank}
                            category={overallRank.name}
                          />
                        )}
                        {monthlyRank && (
                          <MonthlyRankingBadge
                            size="s"
                            year={monthlyRank.year}
                            month={monthlyRank.month}
                            rank={monthlyRank.rank}
                            category={monthlyRank.category}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="h-px w-full bg-[#E7E7E7]" />
                <div className="mt-2 px-8">
                  <p>
                    在分類下搜索關鍵字{' '}
                    <span className="font-medium text-sorra-pink">
                      “{keyword}”
                    </span>
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-x-2 gap-y-4">
                    {_.take(recommendationSecondCategories, 6).map(
                      (secondCategoryId) => {
                        const secondCategoryName = secondCategoryMap[
                          secondCategoryId
                        ]
                          ? secondCategoryMap[secondCategoryId].name
                          : '';
                        return (
                          <div
                            key={secondCategoryId}
                            className="flex h-7 cursor-pointer items-center gap-x-1.5 gap-y-4 rounded-full text-sm font-medium text-[#111928]"
                            onClick={() => {
                              routeToSearchPage(keyword, secondCategoryId);
                              // router.push(
                              //   `/search?product=&category=${secondCategoryName}&categoryID=${secondCategoryId}&keyword=${keyword}`
                              // );
                              // window.location.href = `/search?product=&category=${secondCategoryName}&categoryID=${secondCategoryId}&keyword=${keyword}`;
                            }}
                          >
                            <div className="h-[13px] w-[14px]">
                              <CircleArrowSvg />
                            </div>
                            <p>{secondCategoryName}</p>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {!keyword && (
            <div className="px-8 pb-8 pt-5">
              {searchHistory.length > 0 && (
                <div>
                  <div className="flex justify-between text-base">
                    <p className="font-medium text-[#111928]">搜尋記錄</p>
                    <div className="flex gap-x-3">
                      {showClearHistory && (
                        <>
                          <button
                            className="flex items-center gap-x-1 font-medium text-[#9b9b9b]"
                            onClick={() => {
                              setShowClearHistory(false);
                            }}
                          >
                            <p>取消</p>
                          </button>
                          <div className="h-6 w-px border border-[#9b9b9b] bg-[#9b9b9b]" />
                          <button
                            className="flex items-center gap-x-1 font-medium text-sorra-pink"
                            onClick={async () => {
                              await handleClearSearchHistory();
                            }}
                          >
                            <div className="h-5 w-5">
                              <TrashSvg color="#F16B7C" />
                            </div>
                            <p>確認清除</p>
                          </button>
                        </>
                      )}
                      {!showClearHistory && (
                        <button
                          className="flex items-center gap-x-1 font-medium text-[#9b9b9b]"
                          onClick={() => {
                            setShowClearHistory(true);
                          }}
                        >
                          <div className="h-5 w-5">
                            <TrashSvg />
                          </div>
                          <p>清除</p>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex-rwo mt-4 flex flex-wrap gap-2">
                    {searchHistory.map((search, idx) => {
                      return (
                        <div
                          key={idx}
                          className="block cursor-pointer rounded-full border border-[#E7E7E7] px-3 py-1 text-sm font-medium text-[#4a4a4a]"
                          onClick={() => {
                            routeToSearchPage(search);
                            // router.push(`/search?keyword=${search}`);
                            // window.location.href = `/search?keyword=${search}`;
                          }}
                        >
                          <p>{search}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div
                className={classnames({
                  'mt-6': searchHistory.length > 0,
                })}
              >
                <div className="flex justify-between text-base">
                  <p className="font-medium text-[#111928]">🔥 熱門關鍵字</p>
                  <button
                    className="flex items-center gap-x-1 font-medium text-sorra-pink"
                    onClick={() => {
                      router.push('/ranking');
                      // window.location.href = '/ranking';
                    }}
                  >
                    <div className="h-5 w-5">
                      <RankingSvg />
                    </div>
                    <p>美妝排行榜</p>
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-x-2 gap-y-4">
                  {topSearches.map((search, idx) => {
                    return (
                      <div
                        key={idx}
                        className="flex h-7 cursor-pointer items-center gap-x-1.5 gap-y-4 rounded-full px-0.5 text-sm font-medium text-[#111928]"
                        onClick={() => {
                          routeToSearchPage(search);
                          // router.push(`/search?keyword=${search}`);
                          // window.location.href = `/search?keyword=${search}`;
                        }}
                      >
                        <div className="h-[13px] w-[14px]">
                          <CircleArrowSvg />
                        </div>
                        <p>{search}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchInput;
